import axios from "axios";

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");

export const BACKEND_URL = rawBackendUrl.replace(/\/api$/, "");
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// attach bearer if available (fallback when cookies blocked)
api.interceptors.request.use((config) => {
    const t = localStorage.getItem("legends_access_token");
    if (t && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
});

// Retry failed GET requests up to 3 times with exponential backoff.
// Handles the "preview pod is waking up" case where the first hit times-out
// but subsequent hits (after 1-3s) succeed.
api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const config = error?.config;
        if (!config || (config.method || "get").toLowerCase() !== "get") {
            return Promise.reject(error);
        }
        config.__retry = (config.__retry || 0) + 1;
        // Only retry on network / 5xx / 502 / 503 / 504 (pod waking up)
        const status = error?.response?.status;
        const isWakeable = !error.response || (status >= 500 && status < 600);
        if (!isWakeable || config.__retry > 3) {
            return Promise.reject(error);
        }
        const delay = 500 * Math.pow(2, config.__retry - 1); // 500ms, 1s, 2s
        await new Promise((res) => setTimeout(res, delay));
        return api(config);
    }
);

export function fileUrl(idOrPath) {
    if (!idOrPath) return null;
    const value = String(idOrPath).trim();
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith("/api/files/")) return `${BACKEND_URL}${value}`;
    if (value.startsWith("api/files/")) return `${BACKEND_URL}/${value}`;
    if (value.startsWith("/uploads/")) return `${BACKEND_URL}${value}`;
    if (value.startsWith("uploads/")) return `${BACKEND_URL}/${value}`;
    return `${API}/files/${value.replace(/^\/+/, "")}`;
}

export function uploadUrl(filename) {
    if (!filename) return null;
    const clean = String(filename).replace(/^\/+/, "");
    return `${BACKEND_URL}/uploads/${clean.replace(/^uploads\//, "")}`;
}

export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "حدث خطأ غير متوقع";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .join(" ");
    return String(detail);
}

// Defensive: always return an array from an API payload.
// Handles: raw arrays, { <key>: [...] }, { data: [...] }, { items: [...] }, { results: [...] }.
export const asArray = (payload, key) => {
    if (Array.isArray(payload)) return payload;
    if (key && Array.isArray(payload?.[key])) return payload[key];
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

export default api;
